import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEye } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { ThreeDots } from "react-loader-spinner";
import moment from "moment";
import ActionButton from "../../Common/ActionButton";
import StatusChip, { statusChipType } from "../../Common/StatusChip";
import SearchFilter from "../../Common/SearchFilter";
import ColumnVisibilityControl from "../../Common/ColumnVisibilityControl";
import { useSession } from "../../../context/SessionContext";
import { getallGatepass } from "../../../Redux/actions/Operationsactions/getgatepass";
import { filterStoreProjects } from "../projectFilters";

/**
 * Gate Pass List Table
 * Backend has no Edit/Delete endpoint for Gatepass — list + create only.
 */
const GatePassTable = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { rolesdata } = useSession();
  const projects = filterStoreProjects(rolesdata?.data?.Projects);
  const gatepassData = useSelector((state) => state.getAllGatepass);
  const rolesData = useSelector((state) => state.GetAllRole);

  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selectedProjectId, setSelectedProjectId] = useState("");

  useEffect(() => {
    if (selectedProjectId !== undefined && selectedProjectId !== null) {
      dispatch(getallGatepass(selectedProjectId));
    }
  }, [dispatch, selectedProjectId]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Gate Pass No",
        field: "GatepassNumber",
        flex: 1,
      },
      {
        headerName: "MR No",
        field: "MrNumber",
        flex: 1,
      },
      {
        headerName: "Gate Pass Date",
        valueGetter: (params) =>
          params.data?.GatepassDate ? moment(params.data.GatepassDate).format("MMM DD YYYY") : "",
        flex: 1,
      },
      {
        headerName: "Vehicle #",
        field: "VehicleNumber",
        valueGetter: (params) => params.data?.VehicleNumber || params.data?.VehicleNo || "N/A",
        flex: 1,
      },
      {
        headerName: "Driver Name",
        field: "DriverName",
        valueGetter: (params) => params.data?.DriverName || "N/A",
        flex: 1,
      },
      {
        headerName: "Note / Purpose",
        field: "Note",
        flex: 2,
      },
      {
        headerName: "Status",
        field: "Status",
        flex: 1,
        cellRenderer: (params) => {
          if (!params.data || !params.value) {
            return "N/A";
          }
          return <StatusChip status={params.value} type={statusChipType(params.value)} />;
        },
      },
      {
        headerName: "Created At",
        valueGetter: (params) =>
          params.data?.CreatedAt ? moment(params.data.CreatedAt).format("MMM DD YYYY hh:mm A") : "",
        flex: 1,
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 130,
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {rolesData?.data?.UserRoles?.includes("Store Gate Pass_Create") ? (
              <ActionButton
                text="New+"
                newBtn="true"
                onClick={() => history.push("/store/gate-pass/add")}
              />
            ) : (
              <span>Action</span>
            )}
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <FiEye
              title="View"
              size="1.1em"
              style={{ cursor: "pointer", color: "#4ac8d1" }}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/store/gate-pass/details/${params.data?.Id}`);
              }}
            />
          </div>
        ),
      },
    ],
    [history, rolesData],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 100,
    }),
    [],
  );

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Outward Gate Pass"
        >
          Outward Gate Pass
        </div>
        <div className="d-flex align-items-center" style={{ gap: "6px" }}>
          <label className="input_label m-0" style={{ whiteSpace: "nowrap" }}>
            Project:
          </label>
          <select
            className="input_styling"
            style={{ width: "220px" }}
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            <option value="">Select Project</option>
            {projects.map((p) => (
              <option key={p.ProjectId} value={p.ProjectId}>
                {p.ProjectName}
              </option>
            ))}
          </select>
        </div>

        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
        />
        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
        {gatepassData?.loading && (
          <div>
            <ThreeDots color="green" height={40} width={40} />
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={gatepassData?.data || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export { GatePassTable };
export default GatePassTable;
